import { FeedDataFieldType, MerchandisingRuleTypes } from 'src/services';
import { combineActionAndWeightText } from 'src/components-bl/MerchandisingRules/merchandisingRules.helpers';

const { MerchandisingRuleSubType } = MerchandisingRuleTypes;
type MerchandiseRule = MerchandisingRuleTypes.MerchandiseRule;
type RootCondition = MerchandisingRuleTypes.RootCondition;

// Only the condition shapes the wizard's bounded "New rule" composer can produce, plus the ones
// most commonly seen on existing rules - enough to render a real one-line summary for both jobs
// without pulling in the full MerchandisingRuleForm condition machinery (KPI/schedule/context
// stay in Merchandising Rules, per the wizard's scope).
export function summarizeCondition(condition: RootCondition | undefined): string | undefined {
  if (!condition || !condition.field) return undefined;

  const { field, subType, values } = condition;

  switch (subType) {
    case MerchandisingRuleSubType.Equals: {
      const [value] = values as (string | boolean)[];
      if (typeof value === 'boolean') return `${field} is ${value ? 'true' : 'false'}`;
      return value === undefined ? undefined : `${field} is equal to ${value}`;
    }
    case MerchandisingRuleSubType.EqualsSource:
      return `${field} is equal to source item`;
    case MerchandisingRuleSubType.Contains: {
      const [value] = values as string[];
      return value === undefined ? undefined : `${field} contains ${value}`;
    }
    case MerchandisingRuleSubType.DoesNotContain: {
      const [value] = values as string[];
      return value === undefined ? undefined : `${field} does not contain ${value}`;
    }
    case MerchandisingRuleSubType.HigherThan: {
      const [min] = values as [number | null, number | null];
      return min == null ? undefined : `${field} is more than ${min.toLocaleString()}`;
    }
    case MerchandisingRuleSubType.LowerThan: {
      const [, max] = values as [number | null, number | null];
      return max == null ? undefined : `${field} is less than ${max.toLocaleString()}`;
    }
    case MerchandisingRuleSubType.HasValue:
      return `${field} has a value`;
    case MerchandisingRuleSubType.DoesNotHaveValue:
      return `${field} has no value`;
    case MerchandisingRuleSubType.IsBetween:
    case MerchandisingRuleSubType.RelativeRange: {
      const [min, max] = values as [number | null, number | null];
      return `${field} is between ${min ?? '—'} and ${max ?? '—'}`;
    }
    default:
      return undefined;
  }
}

export function getRuleSummaryText(rule: MerchandiseRule): string {
  const actionText = combineActionAndWeightText(rule);
  const conditionText = summarizeCondition(rule.subRules?.[0]);
  return conditionText ? `${actionText} · ${conditionText}` : actionText;
}

export type ConditionFieldCategory = 'text' | 'numeric' | 'boolean' | 'unsupported';

const numericTypes = new Set([
  FeedDataFieldType.Integer,
  FeedDataFieldType.Long,
  FeedDataFieldType.Float,
]);

export function getConditionFieldCategory(types: FeedDataFieldType[]): ConditionFieldCategory {
  if (types.includes(FeedDataFieldType.Boolean)) return 'boolean';
  if (types.some(type => numericTypes.has(type))) return 'numeric';
  if (types.includes(FeedDataFieldType.Text) || types.includes(FeedDataFieldType.Keyword)) {
    return 'text';
  }
  return 'unsupported';
}

export const SUBTYPE_OPTIONS_BY_CATEGORY: Record<
  ConditionFieldCategory,
  { value: MerchandisingRuleTypes.MerchandisingRuleSubType; text: string }[]
> = {
  text: [
    { value: MerchandisingRuleSubType.Equals, text: 'is equal to' },
    { value: MerchandisingRuleSubType.EqualsSource, text: 'is equal to source item' },
    { value: MerchandisingRuleSubType.Contains, text: 'contains' },
  ],
  numeric: [
    { value: MerchandisingRuleSubType.HigherThan, text: 'is more than' },
    { value: MerchandisingRuleSubType.LowerThan, text: 'is less than' },
  ],
  boolean: [{ value: MerchandisingRuleSubType.Equals, text: 'is' }],
  unsupported: [],
};

export function needsConditionValueInput(
  subType: MerchandisingRuleTypes.MerchandisingRuleSubType | undefined
): boolean {
  return subType !== undefined && subType !== MerchandisingRuleSubType.EqualsSource;
}

// Inverse of `buildConditionValues` - pulls the single raw string the edit drawer's value input
// should start from out of an existing rule's condition, for the subtypes the bounded editor supports.
export function deriveConditionRawValue(
  subType: MerchandisingRuleTypes.MerchandisingRuleSubType | undefined,
  values: MerchandisingRuleTypes.SubRuleOptionalValues | undefined
): string {
  if (!subType || !values) return '';

  switch (subType) {
    case MerchandisingRuleSubType.HigherThan: {
      const [min] = values as [number | null, number | null];
      return min == null ? '' : String(min);
    }
    case MerchandisingRuleSubType.LowerThan: {
      const [, max] = values as [number | null, number | null];
      return max == null ? '' : String(max);
    }
    case MerchandisingRuleSubType.Equals: {
      const [value] = values as (string | boolean)[];
      return value === undefined ? '' : String(value);
    }
    case MerchandisingRuleSubType.Contains:
    case MerchandisingRuleSubType.DoesNotContain: {
      const [value] = values as string[];
      return value ?? '';
    }
    default:
      return '';
  }
}

export function buildConditionValues(
  subType: MerchandisingRuleTypes.MerchandisingRuleSubType,
  category: ConditionFieldCategory,
  rawValue: string
): MerchandisingRuleTypes.SubRuleOptionalValues {
  switch (subType) {
    case MerchandisingRuleSubType.EqualsSource:
      return [];
    case MerchandisingRuleSubType.HigherThan:
      return [rawValue === '' ? null : Number(rawValue), null];
    case MerchandisingRuleSubType.LowerThan:
      return [null, rawValue === '' ? null : Number(rawValue)];
    case MerchandisingRuleSubType.Equals:
      if (category === 'boolean') return [rawValue === 'true'];
      return [rawValue];
    case MerchandisingRuleSubType.Contains:
    case MerchandisingRuleSubType.DoesNotContain:
      return [rawValue];
    default:
      return [];
  }
}
